import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import { useTheme } from "@material-ui/core/styles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";
import { Button, Grid, IconButton, StepContent, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Box } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import LinkIcon from "@material-ui/icons/Link";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import { listQueueOptions, createQueueOption } from "../../services/queueOptions.api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AttachFile, DeleteOutline } from "@material-ui/icons";
import { head } from "lodash";
import { i18n } from "../../translate/i18n";
import TransferQueueModal from "../TransferQueueModal";
import { useStyles } from "./QueueOptionsStyles";
import { QueueOptionStepper } from "./QueueOptionStepper";



export const QueueOptions = forwardRef(({ queueId, onOptionAdded, onOptionsChanged }, ref) => {
  const classes = useStyles();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  
  // ✅ REF PARA CONTROLAR SI EL COMPONENTE ESTÁ MONTADO
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (queueId) {
      const fetchOptions = async () => {
        try {
          const { data } = await listQueueOptions({ queueId, parentId: -1 });
          
          // ✅ VERIFICAR SI EL COMPONENTE SIGUE MONTADO
          if (!isMountedRef.current) return;
          
          const optionList = data.map((option) => {
            return {
              ...option,
              children: [],
              edition: false,
            };
          });

          // ✅ CARGAR TODOS LOS HIJOS RECURSIVAMENTE
          await Promise.all(
            optionList.map(async (opt) => {
              // ✅ VERIFICAR SI EL COMPONENTE SIGUE MONTADO
              if (!isMountedRef.current) return;
              await loadAllChildren(opt, queueId);
            })
          );

          // ✅ VERIFICAR SI EL COMPONENTE SIGUE MONTADO ANTES DE ACTUALIZAR ESTADO
          if (isMountedRef.current) {
            setOptions(optionList);
          }
        } catch (e) {
          // ✅ SOLO MOSTRAR ERROR SI EL COMPONENTE SIGUE MONTADO
          if (isMountedRef.current) {
            console.error("❌ Error al cargar opciones:", e);
            toastError(e);
          }
        }
      };
      fetchOptions();
    }
    
    // ✅ CLEANUP: MARCAR COMO DESMONTADO
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueId]);

  // ✅ Notificar al padre cuando cambia el número de opciones (para habilitar/deshabilitar campos)
  useEffect(() => {
    if (onOptionsChanged && typeof onOptionsChanged === 'function') {
      onOptionsChanged(options.length);
    }
  }, [options.length, onOptionsChanged]);

  // ✅ FUNCIÓN PARA CARGAR HIJOS RECURSIVAMENTE EN INICIAL
  const loadAllChildren = async (opt, qId) => {
    if (!opt.id) return;

    const { data } = await listQueueOptions({ queueId: qId, parentId: opt.id });
    opt.children = data.map((o) => ({
      ...o,
      children: [],
      edition: false,
    }));
    cleanCorruptedData(opt);

    await Promise.all(
      opt.children.map(async (child) => {
        await loadAllChildren(child, qId);
      })
    );
  };

  // ✅ FUNCIÓN PARA LIMPIAR DATOS CORRUPTOS (DUPLICADA PARA QUE FUNCIONE EN QUEUEOPTIONS)
  const cleanCorruptedData = (option) => {
    if (!option.children || !option.id) return;
    
    // ✅ CONVERTIR option.id A NÚMERO PARA COMPARACIÓN CORRECTA
    const optionId = Number(option.id);
    
    const validChildren = option.children.filter(child => {
      // ✅ CONVERTIR child.parentId A NÚMERO Y COMPARAR
      const childParentId = child.parentId !== null && child.parentId !== undefined 
        ? Number(child.parentId) 
        : null;
      
      // ✅ VALIDAR QUE EL parentId COINCIDA CON EL ID DE LA OPCIÓN PADRE
      if (childParentId !== optionId) {
        return false;
      }
      
      // ✅ VALIDAR QUE NO TENGA TÍTULO DUPLICADO DEL PADRE
      if (child.title === option.title) {
        return false;
      }
      
      return true;
    });
    
    if (validChildren.length !== option.children.length) {
      option.children = validChildren;
    }
  };

  const renderStepper = () => {
    if (options.length > 0) {
      return (
        <QueueOptionStepper
          queueId={queueId}
          updateOptions={updateOptions}
          options={options}
          saveAllOptions={saveAllOptions}
        />
      );
    }
  };

  // ✅ FUNCIÓN RECURSIVA PARA GUARDAR OPCIONES Y SUS HIJOS
  const saveOptionRecursive = async (opt, parentId = null, qId) => {
    let id = opt.id;
    if (!id && opt.title.trim() !== "") {
          const { data } = await createQueueOption({
            ...opt,
            queueId: qId,
            parentId: parentId,
          });
      id = data.id;
      opt.id = id;
    }

    if (opt.children && opt.children.length > 0) {
      for (let child of opt.children) {
        await saveOptionRecursive(child, id, qId);
      }
    }
  };

  const saveAllOptions = useCallback(async (savedQueueId) => {
    for (let option of options) {
      await saveOptionRecursive(option, null, savedQueueId);
    }
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOptions = () => {
    setOptions([...options]);
  };



  const addOption = () => {
    // ✅ VALIDAR LÍMITE DE OPCIONES PRINCIPALES
    const maxMainOptions = 10;
    if (options.length >= maxMainOptions) {
      toastError(`Máximo ${maxMainOptions} opciones principales permitidas`);
      return;
    }
    
    // ✅ AGREGAR OPCIÓN PRINCIPAL CON PROFUNDIDAD 1
    const newOption = {
      title: "",
      message: "",
      edition: false,
      option: options.length + 1,
      queueId: queueId || null,
      parentId: null,
      children: [],
      depth: 1, // ✅ PROFUNDIDAD 1 PARA OPCIONES PRINCIPALES
    };
    
    setOptions([...options, newOption]);
    
    // ✅ Notificar al componente padre que se agregó una opción
    if (onOptionAdded && typeof onOptionAdded === 'function') {
      onOptionAdded();
    }
  };

  // ✅ Función para limpiar todas las opciones
  const clearAllOptions = useCallback(() => {
    setOptions([]);
  }, []);

  // ✅ Exponer funciones al componente padre
  useImperativeHandle(ref, () => ({
    saveAllOptions,
    clearAllOptions,
    getOptionsCount: () => options.length
  }));

  return (
    <div className={classes.root}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{ gap: theme.spacing(1), marginBottom: theme.spacing(1) }}
      >
        <Box style={{ flex: "1 1 200px", minWidth: 200 }}>
          <Typography variant="subtitle2" component="div" style={{ fontWeight: 600 }}>
            {i18n.t("queueOptions.title")}
          </Typography>
          <Typography variant="caption" color="textSecondary" component="p" style={{ margin: "4px 0 0 0", lineHeight: 1.45 }}>
            {i18n.t("queueOptions.subtitle")}
          </Typography>
        </Box>
        <Button
          color="primary"
          size="small"
          onClick={addOption}
          startIcon={<AddIcon />}
          variant="outlined"
          disabled={options.length >= 10}
          title={options.length >= 10 ? "Máximo 10 opciones principales alcanzado" : "Agregar opción principal"}
        >
          {options.length >= 10 ? "Límite alcanzado" : i18n.t("queueOptions.add")}
        </Button>
      </Box>
      {renderStepper()}
    </div>
  );
});
